import { Story } from '../../types/story';

export const dublinAffair: Story = {
  id: 'dublin-affair',
  title: 'The Affair',
  excerpt: 'Two married strangers meet at a Dublin hotel bar and make a pact—one night, no names, no consequences.',
  city: 'Dublin',
  publishDate: '2025-12-06',
  readTime: '27 min read',
  tags: ['♀♂', 'affair', 'hotel', 'forbidden'],
  categories: ['forbidden', 'romantic', 'intense'],
  intensity: 3,
  content: `The hotel bar in Dublin was the kind of place where business travelers went to drink alone and pretend they weren't lonely. I'd been sitting there for an hour, nursing a whisky and avoiding going back to my empty room, when she sat down two stools away.

She ordered a gin and tonic and pulled out her phone, the universal signal for "don't talk to me." I respected that. I went back to my drink and my own phone, scrolling through emails I had no intention of answering.

"Are you here for the medical conference too?" she asked after a few minutes.

I looked up. She was beautiful in that understated way that takes a moment to register—dark hair, intelligent eyes, the kind of face that got more interesting the longer you looked at it. And she was wearing a wedding ring. So was I.

"No. Tech conference. Ends tomorrow, thank god."

"Same. Well, not tech. Cardiology. But also ends tomorrow." She took a sip of her drink. "I hate these things. The networking, the small talk, pretending to care about research I'll never use."

"Then why come?"

"Because my husband thinks it's good for my career. And he's probably right. But that doesn't make it less tedious." She paused. "Sorry. That was too much information for a stranger."

"It's fine. I'm a stranger. You'll never see me again after tonight."

"True." She smiled, and it transformed her face. "I'm... actually, let's not do names. Is that weird?"

"Not at all."

We talked for hours. About everything except the things that mattered—our real lives, our marriages, the reasons we were sitting in a hotel bar in Dublin drinking too much and flirting with strangers. She was funny and sharp and clearly as unhappy as I was, though neither of us said it out loud.

"Can I tell you something?" she said, after her third gin. "I love my husband. I do. He's a good man. A good father. But sometimes I feel like I'm disappearing. Like I'm just 'his wife' or 'the kids' mom' or 'Dr. Whatever' and I've forgotten who I actually am underneath all those roles."

"I know exactly what you mean. I've been married fifteen years. We're comfortable. We're partners. But we're not... we're not in love anymore. We're just going through the motions."

"Do you think about leaving?"

"All the time. But then I think about the kids, the house, the life we've built. And I stay. What about you?"

"Same. It's easier to stay than to blow everything up. Even if staying means slowly suffocating."

We sat in silence for a moment, the weight of our admissions hanging between us.

"I have a room upstairs," I said quietly. "Room 412. If you wanted to... I don't know. Talk more. Have another drink."

She looked at me for a long moment. "We shouldn't."

"I know."

"We're married. This is wrong."

"I know."

"But I'm going to say yes anyway."

We took separate elevators, met in the hallway outside my room. I fumbled with the key card, my hands shaking slightly. Once inside, we stood awkwardly, the reality of what we were about to do settling over us.

"We don't have to do anything," I said. "We can just talk."

"I don't want to just talk." She stepped closer, her hand coming up to touch my face. "I want to feel something. I want to remember what it's like to be wanted, not needed. To be seen as a woman, not just a wife or a mother or a doctor."

I kissed her, and she kissed me back with a desperation that matched my own. We undressed each other quickly, urgently, falling onto the bed in a tangle of limbs and need.

"Tell me what you want," I said, my hand between her legs, feeling how wet she already was.

"Everything. I want everything."

I took my time with her, exploring her body like it was a map to somewhere I'd been trying to find for years. She was responsive and vocal, telling me what felt good, what she needed more of, guiding me with her hands and her words.

When I finally entered her, she gasped, her nails digging into my back. "God, I'd forgotten what this feels like."

"What?"

"Being present. Being here, in my body, in this moment. Not thinking about tomorrow or yesterday or all the things I should be doing. Just... this."

We moved together, finding a rhythm, and it was intense and intimate in a way that had nothing to do with love and everything to do with recognition. We were two people who'd lost themselves somewhere along the way, finding each other in the wreckage.

She came first, crying out, her whole body tensing. I followed soon after, collapsing beside her, both of us breathing hard.

"I should feel guilty," she said after a while. "But I don't. Is that terrible?"

"I don't know. I don't feel guilty either. Maybe we're both terrible people."

"Or maybe we're just human. Maybe this is what happens when you spend years being what everyone else needs you to be and forget to be what you need for yourself."

We made love again, slower this time, savoring it. And then we lay in the dark, talking about nothing and everything—our childhoods, our dreams, the people we'd been before marriage and kids and mortgages turned us into responsible adults.

"What happens now?" she asked as the sun started to rise.

"Nothing. We go back to our lives. Our marriages. Our responsibilities. We pretend this never happened."

"Will you tell your wife?"

"No. Will you tell your husband?"

"No. What would be the point? It would only hurt them, and for what? To make ourselves feel better? To ease our guilt?"

"So we just carry this secret."

"We carry this secret." She sat up, started gathering her clothes. "But I want you to know—this meant something. Not love, but something. A reminder that I'm still alive under all the roles I play. That I'm still capable of feeling something."

I watched her dress, memorizing her face, knowing I'd never see her again. "Thank you. For tonight. For understanding."

"Thank you too." She leaned down and kissed me one last time. "I hope you find what you're looking for. I hope we both do."

She left, and I lay in the rumpled bed, watching the Dublin sky turn from black to grey to pale blue. I felt different. Not better, exactly, but more awake. More aware of the choices I'd been making, the life I'd been accepting without question.

I flew home that afternoon and went back to my marriage, my job, my routine. But something had shifted. I started therapy. Started having honest conversations with my wife about what we both wanted, what we'd lost, whether we could find it again. It was hard and uncomfortable and sometimes painful, but it was real in a way we hadn't been real with each other in years.

We're still together. We're working on it. Some days are better than others. But I'm grateful for that night in Dublin, for the stranger who reminded me that I was more than just the roles I played. That I was still capable of feeling, of wanting, of being present in my own life.

I don't know what happened to her. I hope she found what she was looking for too. I hope she's happy, or at least happier. I hope she knows that she gave me something precious that night—not just pleasure, but permission. Permission to want more. Permission to be honest about what wasn't working. Permission to try to fix it instead of just accepting it.

And sometimes, that's the most intimate gift you can give someone. Not love, but truth. Not forever, but right now. Not promises, but presence.

That night in Dublin wasn't about the sex, though the sex was good. It was about two people finding each other in the dark and saying, "I see you. I see who you really are under all the masks you wear. And you're not alone."

And sometimes, that's enough.`
};
